import React, { memo, useCallback, useMemo, useState } from 'react';

import SecondaryTabGroupCDS from '@sharedCR/coreDesignSystem/SecondaryTabGroupCDS';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  CIC_DISCLOSURE_FILTER_BUTTONS,
  CicDisclosureFilter,
  NO_FILTERED_DISCLOSURES_MESSAGE,
  NO_SENT_DISCLOSURES_MESSAGE,
  SENT_DISCLOSURES_TITLE,
} from '../../LoanCicHistory.constants';
import { LoanCicHistoryTestIds } from '../../LoanCicHistory.testIds';
import { buildDisclosureRows, filterDisclosureRows } from '../../LoanCicHistory.utils';
import CicEmptyMessage from '../CicEmptyMessage';
import CicSectionCard from '../CicSectionCard';

import { getCicDisclosuresTableConfig } from './CicSentDisclosures.config';
import CicDisclosureDetails from './components/CicDisclosureDetails';

const CicSentDisclosures = ({
  disclosures,
  isEditAllowed,
  onEditFeeChange,
  onOpenHistory,
  children = null,
}) => {
  const [filter, setFilter] = useState(CicDisclosureFilter.ALL);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const rows = useMemo(() => buildDisclosureRows(disclosures), [disclosures]);
  const filteredRows = useMemo(() => filterDisclosureRows(rows, filter), [rows, filter]);

  const config = useMemo(() => getCicDisclosuresTableConfig({ expandedRowId }), [expandedRowId]);

  const expandedDisclosure = useMemo(
    () => filteredRows.find(({ id }) => id === expandedRowId) ?? null,
    [filteredRows, expandedRowId],
  );

  const expandedRowContent = useMemo(
    () =>
      expandedDisclosure && (
        <CicDisclosureDetails
          disclosure={expandedDisclosure}
          isEditAllowed={isEditAllowed}
          onEditFeeChange={onEditFeeChange}
          onOpenHistory={onOpenHistory}
        />
      ),
    [expandedDisclosure, isEditAllowed, onEditFeeChange, onOpenHistory],
  );

  const onRowClick = useCallback(rowId => {
    setExpandedRowId(previousRowId => (previousRowId === rowId ? null : rowId));
  }, []);

  const filterGroup = useMemo(
    () =>
      !_.isEmpty(rows) && (
        <SecondaryTabGroupCDS
          buttons={CIC_DISCLOSURE_FILTER_BUTTONS}
          data-testid={LoanCicHistoryTestIds.FILTER_GROUP}
          value={filter}
          onChange={setFilter}
        />
      ),
    [rows, filter],
  );

  const emptyMessage = _.isEmpty(rows)
    ? NO_SENT_DISCLOSURES_MESSAGE
    : NO_FILTERED_DISCLOSURES_MESSAGE;

  return (
    <CicSectionCard
      dataTestId={LoanCicHistoryTestIds.SENT_DISCLOSURES}
      headerAside={filterGroup}
      title={SENT_DISCLOSURES_TITLE}
    >
      {_.isEmpty(filteredRows) ? (
        <CicEmptyMessage
          dataTestId={LoanCicHistoryTestIds.SENT_EMPTY_LABEL}
          message={emptyMessage}
        />
      ) : (
        <TableCDS
          additionalRowContent={expandedRowContent}
          additionalRowContentRowId={expandedRowId}
          config={config}
          data={filteredRows}
          onRowClick={onRowClick}
        />
      )}
      {children}
    </CicSectionCard>
  );
};

CicSentDisclosures.propTypes = {
  disclosures: PropTypes.arrayOf(
    PropTypes.shape({
      formDeliveryRequestId: PropTypes.string.isRequired,
      bundleType: PropTypes.string,
      sentDate: PropTypes.string,
      deliveryStatus: PropTypes.string,
      documents: PropTypes.arrayOf(
        PropTypes.shape({ documentId: PropTypes.string, name: PropTypes.string }),
      ),
      carried: PropTypes.array,
      removed: PropTypes.array,
    }),
  ).isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  onEditFeeChange: PropTypes.func.isRequired,
  onOpenHistory: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default memo(CicSentDisclosures);
